import { Icon, Form, FormApi, If, onMount, reactive } from 'cdui-js';
import {
  changeFile,
  lastProcess,
  nextProcess,
  pState,
  setFormApi,
  setFormValidateFunc,
} from '../../store';
import { busConfigs } from '../../../../../../stores/bus-config';
import { userState } from '../../../../../../stores/user';
import { UploadIdentity } from '../../UploadIdentity';
import { SelectBox } from '../../../../components/SelectBox';
import { SelectCountry } from '../../../../components/SelectCountry';
import { LastNextBtns } from '../../components/LastNextBtns';
import { isOpenProgressReEdit } from '../../reEditMode';
import { commonState } from '../../../../../../stores/common';
import { aesEncryptHexByPass } from '../../../../../../lib/crypto';
import { trackKycIdOcrResult } from '../../../../../../analytics';
import { saveCareer, saveUserInfo, verifyCertNoTwoElement } from '../../../../../../api/open-account';
import { showHud, showToast } from '../../../../components/Toast';
import { DatePicker } from '../../../../components/DatePicker';
import { OpenFormItem } from '../../components/OpenFormItem';
import { FieldReEditHint } from '../../components/FieldReEditHint';
import { DiFiInput } from '../../../../../../components/DiFi/DiFiInput';
import { DiFiCheckBox } from '../../../../../../components/DiFi/DiFiCheckBox';
import i18n from '../../../../../../i18n';
import { applyNationalityForIdentity, syncTaxFieldsFromLic } from './taxSync';
import { applyJoinedNames, fillOcrNameParts, hasRequiredName } from '../../../nameParts';
import { preloadUploadLicExampleImages } from '../idlePreload';
import {
  createIdCardEndDateRules,
  getIdCardEndDateError,
  syncIdCardDateRange,
} from './idCardValidityDates';

import { consoleLog } from '../../../../../../lib/utils';

/** /user/info 注册邀请码（只读展示，不提交开户表单） */
const registeredInviteCode = () => {
  const info = userState.userInfo as Record<string, any> | null;
  if (!info) return '';
  const raw = info.inviteCode ?? info.userMsg?.inviteCode;
  return String(raw ?? '').trim();
};

const getLicDateRules = () =>
  createIdCardEndDateRules(
    () => pState.uploadLicForm.idCardStartDate,
    () => pState.uploadLicForm.idCardLongTerm,
  );

/**
 * 到期日提交校验（非重复逻辑，补 Form 缺口）。
 *
 * 背景：编辑资料 / 续办回填时，idCardEndDate 已有值；cdui Form 对 required 字段
 * 只验「非空」，不跑 rules.onvalidate。用户若不重选日期，仅靠 Form 拦不住
 * 「已过期 / 剩余不足1个月 / 不晚于起始」。选日期时的 DatePicker.change 拦的是
 * 新选值，覆盖不了回填旧值，故提交前必须再验一遍。
 */
const assertIdCardEndDateOk = (): boolean => {
  const err = getIdCardEndDateError(
    pState.uploadLicForm.idCardStartDate,
    pState.uploadLicForm.idCardEndDate,
    pState.uploadLicForm.idCardLongTerm,
  );
  if (err) {
    showHud(err);
    return false;
  }
  return true;
};

const getExpList = () => [
  { img: '/images/open-exp1.png', text: i18n.OpenProgress.Info.UploadLic.RejectMainland1 },
  { img: '/images/open-exp2.png', text: i18n.OpenProgress.Info.UploadLic.RejectMainland2 },
  { img: '/images/open-exp3.png', text: i18n.OpenProgress.Info.UploadLic.RejectMainland3 },
  { img: '/images/open-exp4.png', text: i18n.OpenProgress.Info.UploadLic.RejectMainland4 },
];

const getExpList2 = () => [
  { img: '/images/open-exp1-hk.png', text: i18n.OpenProgress.Info.UploadLic.RejectHk1 },
  { img: '/images/open-exp2-hk.png', text: i18n.OpenProgress.Info.UploadLic.RejectHk2 },
  { img: '/images/open-exp3-hk.png', text: i18n.OpenProgress.Info.UploadLic.RejectHk3 },
  { img: '/images/open-exp4-hk.png', text: i18n.OpenProgress.Info.UploadLic.RejectHk4 },
];

const getExpList3 = () => [
  { img: '/images/open-exp1-mo.png', text: i18n.OpenProgress.Info.UploadLic.RejectMo1 },
  { img: '/images/open-exp2-mo.png', text: i18n.OpenProgress.Info.UploadLic.RejectMo2 },
  { img: '/images/open-exp3-mo.png', text: i18n.OpenProgress.Info.UploadLic.RejectMo3 },
  { img: '/images/open-exp4-mo.png', text: i18n.OpenProgress.Info.UploadLic.RejectMo4 },
];

const getExpList4 = () => [
  { img: '/images/open-exp1-other.png', text: i18n.OpenProgress.Info.UploadLic.RejectOther1 },
  { img: '/images/open-exp2-other.png', text: i18n.OpenProgress.Info.UploadLic.RejectOther2 },
  { img: '/images/open-exp3-other.png', text: i18n.OpenProgress.Info.UploadLic.RejectOther3 },
  { img: '/images/open-exp4-other.png', text: i18n.OpenProgress.Info.UploadLic.RejectOther4 },
];

const getExpListMap = () => ({
  MAINLAND_CHINA: getExpList(),
  HONG_KONG: getExpList2(),
  MACAO: getExpList3(),
  OTHER_REGION: getExpList4(),
});

const getTitleMap = () => ({
  MAINLAND_CHINA: i18n.OpenProgress.Info.UploadLic.TitleMainland,
  HONG_KONG: i18n.OpenProgress.Info.UploadLic.TitleHongKong,
  MACAO: i18n.OpenProgress.Info.UploadLic.TitleMacao,
  OTHER_REGION: i18n.OpenProgress.Info.UploadLic.TitlePassport,
});

const formatOcrDateParts = (y: string, mo: string, d: string): string =>
  `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;

const parseOcrBirthDate = (raw?: string, idNumber?: string): string => {
  if (raw) {
    const cn = raw.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/);
    if (cn) {
      const [, y, mo, d] = cn;
      return formatOcrDateParts(y, mo, d);
    }
    const iso = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (iso) {
      const [, y, mo, d] = iso;
      return formatOcrDateParts(y, mo, d);
    }
  }
  if (idNumber?.length === 18) {
    return `${idNumber.slice(6, 10)}-${idNumber.slice(10, 12)}-${idNumber.slice(12, 14)}`;
  }
  return '';
};

const parseOcrGender = (sex?: string, idNumber?: string): 'MALE' | 'FEMALE' | '' => {
  if (sex) {
    const s = sex.trim();
    const hit = busConfigs.gender.find((g: { scDesc?: string; tcDesc?: string; enDesc?: string; label?: string; code?: string }) =>
      [g.scDesc, g.tcDesc, g.enDesc, g.label].includes(s),
    );
    if (hit?.code === 'MALE' || hit?.code === 'FEMALE') return hit.code;
  }
  if (idNumber?.length === 18) {
    const n = +idNumber[16];
    if (!Number.isNaN(n)) return n % 2 === 1 ? 'MALE' : 'FEMALE';
  }
  return '';
};

export const UploadLic = (props) => {
  let formApi: FormApi = null;

  const state = reactive({
    loading: false,
  });

  const frontLic = () => {
    const item = pState.attachmentList?.find((i) => i.busId === pState.id && i.attachType === 'identity_front');
    return item?.fileUrl || '';
  };

  const backLic = () => {
    const item = pState.attachmentList?.find((i) => i.busId === pState.id && i.attachType === 'identity_back');
    return item?.fileUrl || '';
  };

  const titleMap = getTitleMap();
  const expListMap = getExpListMap();

  onMount(() => {
    // LastNextBtns：Form 通过后走此回调——专门拦「编辑资料回填」的旧到期日
    setFormValidateFunc({ callback: async () => assertIdCardEndDateOk() });
    preloadUploadLicExampleImages(pState.uploadLicForm.idCardType);
    if (!(pState.uploadLicForm.nationality || '').trim()) {
      applyNationalityForIdentity(pState.uploadLicForm.idCardType);
    }
  });

  return (
    <div class="padding-x-L margin-y-XL">
      <div class="font-s-L font-bold color-D margin-b-XS">
        {i18n.OpenProgress.Info.UploadLic.Title}
        {props.stepText}
      </div>
      <div class="color-D font-s margin-b-XS">{titleMap[pState.uploadLicForm.idCardType]}</div>
      <div class="margin-b-L">{i18n.OpenProgress.Info.UploadLic.ShootGuide}</div>

      <div>
        <div class="color-error margin-b-XS">{i18n.OpenProgress.Info.UploadLic.RejectWarning}</div>

        <div class="flex flex-wrap gap-12px margin-b-L">
          {expListMap[pState.uploadLicForm.idCardType].map((item) => {
            return (
              <div style={{ width: '48%' }}>
                {/* 外层占位：iOS 上比单独给 img 设 aspect-ratio 更稳，加载中不改可滚高度 */}
                <div
                  class="bg-c-XD round-XS"
                  style={{ 'aspect-ratio': '182 / 110', overflow: 'hidden' }}
                >
                  <img
                    src={item.img}
                    width={182}
                    height={110}
                    decoding="async"
                    draggable={false}
                    class="width-100"
                    style={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      'object-fit': 'cover',
                      'pointer-events': 'none',
                    }}
                  />
                </div>
                <div class="flex align-center gap-4px color-D">
                  <Icon name="common-close" class="icon-c-primary icon-s-L icon-s-XXS"></Icon>
                  {item.text}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <UploadIdentity
        type="identity_front"
        img={frontLic()}
        busId={pState.id}
        isOCR={pState.uploadLicForm.idCardType === 'MAINLAND_CHINA'}
        change={({ url, data }) => {
          changeFile('identity_front', url);
          if (data?.ocrResult) {
            const ocr = data.ocrResult;
            fillOcrNameParts(pState.uploadLicForm, ocr);
            pState.uploadLicForm.idCardNo = ocr.idNumber;
            const birthDate = parseOcrBirthDate(ocr.birthDate, ocr.idNumber);
            if (birthDate) pState.uploadLicForm.birthDate = birthDate;
            const gender = parseOcrGender(ocr.sex, ocr.idNumber);
            if (gender) pState.uploadLicForm.gender = gender;
            trackKycIdOcrResult({ doc_type: 'ID', result: 'success' });
          } else if (url) {
            trackKycIdOcrResult({ doc_type: 'ID', result: 'fail', fail_reason: 'ocr_empty' });
          }
        }}
      />
      <FieldReEditHint field="identity_front" />

      <UploadIdentity
        type="identity_back"
        class="margin-t-L"
        img={backLic()}
        busId={pState.id}
        isOCR={pState.uploadLicForm.idCardType === 'MAINLAND_CHINA'}
        change={({ url, data }) => {
          changeFile('identity_back', url);
          if (data?.ocrResult) {
            const dates = (data.ocrResult?.validPeriod || '').split('-');
            pState.uploadLicForm.idCardIssueAuthority = data?.ocrResult.issueAuthority || '';
            if (dates.length) {
              pState.uploadLicForm.idCardStartDate = dates[0];
              pState.uploadLicForm.idCardEndDate = dates[1] || '';
              if (dates[0]) {
                pState.uploadLicForm.idCardLongTerm = 0;
              }
              syncIdCardDateRange(pState.uploadLicForm);
            }
            trackKycIdOcrResult({ doc_type: 'ID', result: 'success' });
          } else if (url) {
            trackKycIdOcrResult({ doc_type: 'ID', result: 'fail', fail_reason: 'ocr_empty' });
          }
        }}
      />
      <FieldReEditHint field="identity_back" />

      <div class="margin-t-L">
        <div class="color-D font-s font-bold">{i18n.OpenProgress.Info.UploadLic.IdInfoSection}</div>

        <Form
          data={pState.uploadLicForm}
          rules={getLicDateRules()}
          align="top"
          api={(api) => {
            formApi = api;
            setFormApi(api);
          }}
        >
          {/* 中文名字，或英文名字（英文姓氏可空） */}
          <OpenFormItem
            label={i18n.OpenProgress.Info.UploadLic.LabelFullName}
            field="fullName"
            class="margin-t-L"
          >
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={50}
              placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
              value={pState.uploadLicForm.fullName}
              onUpdateValue={(v) => {
                pState.uploadLicForm.fullName = v;
              }}
            />
          </OpenFormItem>

          <OpenFormItem
            label={i18n.OpenProgress.Info.UploadLic.LabelFirstNameEn}
            field="firstNameEn"
            class="margin-t-L"
          >
            <DiFiInput
              shellClass="f-input-2"
              inputKind="english"
              maxLength={50}
              placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
              value={pState.uploadLicForm.firstNameEn}
              onUpdateValue={(v) => {
                pState.uploadLicForm.firstNameEn = v;
                applyJoinedNames(pState.uploadLicForm);
              }}
            />
          </OpenFormItem>

          <OpenFormItem
            label={i18n.OpenProgress.Info.UploadLic.LabelLastNameEn}
            field="lastNameEn"
            class="margin-t-L"
          >
            <DiFiInput
              shellClass="f-input-2"
              inputKind="english"
              maxLength={50}
              placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
              value={pState.uploadLicForm.lastNameEn}
              onUpdateValue={(v) => {
                pState.uploadLicForm.lastNameEn = v;
                applyJoinedNames(pState.uploadLicForm);
              }}
            />
          </OpenFormItem>

          <OpenFormItem label={i18n.OpenProgress.Info.UploadLic.LabelIdNo} field="idCardNo" required class="margin-t-L">
            {/* <TextBox class="f-input-2" placeholder={i18n.OpenProgress.Common.PlaceholderEnter}></TextBox> */}
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={50}
              placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
              value={pState.uploadLicForm.idCardNo}
              onUpdateValue={(v) => (pState.uploadLicForm.idCardNo = v)}
            />
          </OpenFormItem>

          <OpenFormItem
            label={i18n.OpenProgress.Info.UploadLic.LabelIssueAuthority}
            field="idCardIssueAuthority"
            required={pState.uploadLicForm.idCardType === 'MAINLAND_CHINA'}
            class="margin-t-L"
          >
            {/* <TextBox class="f-input-2" placeholder={i18n.OpenProgress.Common.PlaceholderEnter}></TextBox> */}
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={50}
              placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
              value={pState.uploadLicForm.idCardIssueAuthority}
              onUpdateValue={(v) => (pState.uploadLicForm.idCardIssueAuthority = v)}
            />
          </OpenFormItem>

          {pState.uploadLicForm.idCardLongTerm !== 1 && (
            <div class="flex margin-t-L gap-16px">
              <OpenFormItem
                label={i18n.OpenProgress.Info.UploadLic.LabelStartDate}
                field="idCardStartDate"
                required
                class="flex-auto"
              >
                <DatePicker
                  value={pState.uploadLicForm.idCardStartDate}
                  placeholder={i18n.OpenProgress.Common.PlaceholderSelect}
                  title={i18n.OpenProgress.Info.UploadLic.LabelStartDate}
                  maxYear={2099}
                  change={(v) => {
                    pState.uploadLicForm.idCardStartDate = v;
                    pState.uploadLicForm.idCardLongTerm = 0;
                    syncIdCardDateRange(pState.uploadLicForm);
                  }}
                />
              </OpenFormItem>
              <OpenFormItem
                label={i18n.OpenProgress.Info.UploadLic.LabelEndDate}
                field="idCardEndDate"
                required
                class="flex-auto"
              >
                <DatePicker
                  value={pState.uploadLicForm.idCardEndDate}
                  placeholder={i18n.OpenProgress.Common.PlaceholderSelect}
                  title={i18n.OpenProgress.Info.UploadLic.LabelEndDate}
                  maxYear={2099}
                  change={(v) => {
                    const err = getIdCardEndDateError(
                      pState.uploadLicForm.idCardStartDate,
                      v,
                      0,
                    );
                    if (err) {
                      showHud(err);
                      return;
                    }
                    pState.uploadLicForm.idCardEndDate = v;
                    pState.uploadLicForm.idCardLongTerm = 0;
                  }}
                />
              </OpenFormItem>
            </div>
          )}

          <DiFiCheckBox
            type="checkbox"
            label={i18n.OpenProgress.Common.LongTermValid}
            checked={pState.uploadLicForm.idCardLongTerm === 1}
            class="font-s-S color-D padding-y-XS"
            style={{ width: '100%' }}
            onChange={() => {
              const next = pState.uploadLicForm.idCardLongTerm !== 1;
              pState.uploadLicForm.idCardLongTerm = next ? 1 : 0;
              if (next) {
                pState.uploadLicForm.idCardStartDate = '';
                pState.uploadLicForm.idCardEndDate = '';
              }
            }}
          />

          <OpenFormItem
            label={i18n.OpenProgress.Info.UploadLic.LabelBirthDate}
            field="birthDate"
            required
            class="margin-t-L"
          >
            <DatePicker
              value={pState.uploadLicForm.birthDate}
              placeholder={i18n.OpenProgress.Common.PlaceholderBirthDate}
              change={(v) => (pState.uploadLicForm.birthDate = v)}
            />
          </OpenFormItem>

          <OpenFormItem label={i18n.OpenProgress.Info.UploadLic.LabelGender} field="gender" required class="margin-t-L">
            <SelectBox
              value={pState.uploadLicForm.gender}
              title={i18n.OpenProgress.Info.UploadLic.LabelGender}
              dict="gender"
              change={(e) => (pState.uploadLicForm.gender = e)}
            />
          </OpenFormItem>

          <OpenFormItem
            label={i18n.OpenProgress.Info.UploadLic.LabelNationality}
            field="nationality"
            required
            class="margin-t-L"
          >
            <SelectCountry
              title={i18n.OpenProgress.Info.UploadLic.LabelNationality}
              value={pState.uploadLicForm.nationality}
              change={(e) => (pState.uploadLicForm.nationality = e)}
            />
          </OpenFormItem>

          <OpenFormItem
            label={i18n.OpenProgress.Info.UploadLic.LabelBirthCountry}
            field="birthCountry"
            required
            class="margin-t-L"
          >
            <SelectCountry
              title={i18n.OpenProgress.Info.UploadLic.LabelBirthCountry}
              value={pState.uploadLicForm.birthCountry}
              change={(e) => (pState.uploadLicForm.birthCountry = e)}
            />
          </OpenFormItem>

          <OpenFormItem
            label={i18n.OpenProgress.Info.UploadLic.LabelInviteCode}
            field="inviteCode"
            class="margin-t-L"
          >
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              disabled
              placeholder={i18n.OpenProgress.Info.UploadLic.InviteCodePlaceholder}
              value={registeredInviteCode()}
            />
          </OpenFormItem>
        </Form>
      </div>

      {/* 退回修改由 EditErrorInfo 自管底栏；勿再注册 LastNextBtns/barSlot */}
      <If when={!isOpenProgressReEdit(props)}>
        <LastNextBtns
          loading={state.loading}
          last={() => {
            lastProcess();
          }}
          next={async () => {
            if (!frontLic()) {
              showToast(i18n.OpenProgress.Info.UploadLic.ToastFrontRequired);
              return;
            }

            if (!backLic()) {
              showToast(i18n.OpenProgress.Info.UploadLic.ToastBackRequired);
              return;
            }

            if (!hasRequiredName(pState.uploadLicForm)) {
              showToast(i18n.OpenProgress.Info.UploadLic.ToastNameRequired);
              return;
            }
            applyJoinedNames(pState.uploadLicForm);

            const valid = await formApi.validate();

            if (!valid) {
              return;
            }

            // 同上：编辑资料回填场景；挂在 next 内防直调本页 next 时绕过 setFormValidateFunc
            if (!assertIdCardEndDateOk()) {
              return;
            }

            try {
              consoleLog(pState.uploadLicForm.idCardType);

              const data = JSON.parse(JSON.stringify(pState.uploadLicForm));
              data.identityType = data.identityType || data.idCardType;
              state.loading = true;

              if (pState.uploadLicForm.idCardType === 'MAINLAND_CHINA') {
                const certName = (data.fullName || data.pinyinName || '').trim();
                const _v = await verifyCertNoTwoElement({ certName, certNo: data.idCardNo });

                if (_v.isConsistent !== '1') {
                  trackKycIdOcrResult({
                    doc_type: 'ID',
                    result: 'fail',
                    fail_reason: 'two_element_mismatch',
                  });
                  showToast(i18n.OpenProgress.Info.UploadLic.ToastInvalidId);
                  state.loading = false;
                  return;
                }
              }

              console.log('[encryptKey][beforeEncrypt][idCardNo]', {
                encryptKey: commonState.encryptKey || '(empty)',
                plainLen: String(data.idCardNo || '').length,
              });
              data.idCardNo = aesEncryptHexByPass(data.idCardNo, commonState.encryptKey);
              await saveUserInfo({ ...data, progress: 3 });
              // 确认页改证件：按证件同步税务并落库（区间可能续到银联页，不限于末页）
              if (pState.editMode) {
                syncTaxFieldsFromLic();
                await saveCareer({ ...pState.taxInfoForm, progress: 8 });
              }
              nextProcess();
            } catch {
              state.loading = false;
            } finally {
              state.loading = false;
            }
          }}
        />
      </If>
    </div>
  );
};
